import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleExclamation,
  faClockRotateLeft,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { upgradeToPro } from "../../../redux/userSlice";
import { useLazyGetPaymentStatusQuery } from "../../../redux/service/paymntService";
import "./style.css";

const STATUS = {
  CHECKING: "checking",
  SUCCESS: "success",
  PENDING: "pending",
  ERROR: "error",
  MISSING_ORDER: "missing-order",
};

export default function PayHereReturn() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [status, setStatus] = useState(STATUS.CHECKING);
  const [attempt, setAttempt] = useState(0);
  const [orderId, setOrderId] = useState(null);
  const [triggerGetStatus] = useLazyGetPaymentStatusQuery();

  const maxTries = 12;

  const pollStatus = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const id =
      urlParams.get("order_id") ||
      localStorage.getItem("payhere_last_order_id");

    setOrderId(id);

    if (!id) {
      setStatus(STATUS.MISSING_ORDER);
      return;
    }

    setStatus(STATUS.CHECKING);
    setAttempt(0);

    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      setAttempt(tries);

      try {
        const json = await triggerGetStatus(id).unwrap();

        if (json?.success && json?.data?.isProUser === true) {
          clearInterval(timer);
          dispatch(upgradeToPro());
          localStorage.removeItem("payhere_last_order_id");
          setStatus(STATUS.SUCCESS);
          return;
        }

        if (tries >= maxTries) {
          clearInterval(timer);
          setStatus(STATUS.PENDING);
        }
      } catch (e) {
        if (tries >= maxTries) {
          clearInterval(timer);
          setStatus(STATUS.ERROR);
        }
      }
    }, 2000);

    return () => clearInterval(timer);
  }, [dispatch, triggerGetStatus]);

  useEffect(() => {
    const cleanup = pollStatus();
    return cleanup;
  }, [pollStatus]);

  useEffect(() => {
    if (status !== STATUS.SUCCESS) return;
    const t = setTimeout(() => navigate("/"), 4000);
    return () => clearTimeout(t);
  }, [status, navigate]);

  const progress = Math.min(100, Math.round((attempt / maxTries) * 100));

  return (
    <div className="phrOverlay">
      <div className="phrCard">
        {status === STATUS.CHECKING && (
          <>
            <div className="phrIconWrap phrChecking">
              <FontAwesomeIcon icon={faSpinner} spin />
            </div>
            <h5 className="phrTitle">Confirming your payment</h5>
            <p className="phrSubtitle">
              Please wait while we verify your payment with PayHere. This
              usually takes a few seconds.
            </p>
            <div className="phrProgressTrack">
              <div
                className="phrProgressFill"
                style={{ width: `${progress}%` }}
              />
            </div>
            {orderId && <p className="phrOrderId">Order #{orderId}</p>}
          </>
        )}

        {status === STATUS.SUCCESS && (
          <>
            <div className="phrIconWrap phrSuccess">
              <FontAwesomeIcon icon={faCircleCheck} />
            </div>
            <h5 className="phrTitle">Payment successful!</h5>
            <p className="phrSubtitle">
              Your Pro account has been activated. Enjoy full access to all
              features.
            </p>
            <button
              type="button"
              className="phrPrimaryBtn"
              onClick={() => navigate("/")}
            >
              Go to Home
            </button>
            <p className="phrHint">Redirecting automatically in a moment…</p>
          </>
        )}

        {status === STATUS.PENDING && (
          <>
            <div className="phrIconWrap phrPending">
              <FontAwesomeIcon icon={faClockRotateLeft} />
            </div>
            <h5 className="phrTitle">Payment not confirmed yet</h5>
            <p className="phrSubtitle">
              We haven't received confirmation from PayHere yet. This can
              take a little longer sometimes — you can check again or come
              back later, your account will update automatically once it's
              confirmed.
            </p>
            {orderId && <p className="phrOrderId">Order #{orderId}</p>}
            <div className="phrActions">
              <button
                type="button"
                className="phrGhostBtn"
                onClick={() => navigate("/")}
              >
                Go to Home
              </button>
              <button
                type="button"
                className="phrPrimaryBtn"
                onClick={pollStatus}
              >
                Check again
              </button>
            </div>
          </>
        )}

        {status === STATUS.ERROR && (
          <>
            <div className="phrIconWrap phrError">
              <FontAwesomeIcon icon={faCircleExclamation} />
            </div>
            <h5 className="phrTitle">Couldn't check payment status</h5>
            <p className="phrSubtitle">
              Something went wrong while contacting our server. Please check
              your connection and try again.
            </p>
            <div className="phrActions">
              <button
                type="button"
                className="phrGhostBtn"
                onClick={() => navigate("/")}
              >
                Go to Home
              </button>
              <button
                type="button"
                className="phrPrimaryBtn"
                onClick={pollStatus}
              >
                Retry
              </button>
            </div>
          </>
        )}

        {status === STATUS.MISSING_ORDER && (
          <>
            <div className="phrIconWrap phrError">
              <FontAwesomeIcon icon={faCircleExclamation} />
            </div>
            <h5 className="phrTitle">Order not found</h5>
            <p className="phrSubtitle">
              We couldn't find an order to check. If you just completed a
              payment, please contact support with your order details.
            </p>
            <button
              type="button"
              className="phrPrimaryBtn"
              onClick={() => navigate("/subscription")}
            >
              Back to Plans
            </button>
          </>
        )}
      </div>
    </div>
  );
}
